import { type AxiosPromise } from "axios";
import { PhosphorFloppyButton, PhosphorTrashButton } from "components/Atoms/PhosphorIconButton";
import { Loading } from "components/panels";
import React from "react";
import { useAddFilterPreset, useLoadFilterPresets, useRemoveFilterPreset } from "utilities/api/filterPresets";
import { InputBase } from "components/Atoms/formInputs/InputBase";

interface Props<TInstruction> {
  cacheKey: string
  filterSet: server.dto.FilterSets
  listApi: () => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.FilterPreset<TInstruction>[]>>
  mutateApi: (instruction: server.dto.CreateFilterPresetInstruction<TInstruction>) => AxiosPromise<any>
  instruction: TInstruction
  onApplyPreset: (preset: server.dto.FilterPreset<TInstruction>) => void
  onClose: () => void
}

const FilterPresetContent = <TInstruction extends unknown>(props: Props<TInstruction>) => {
  const [filterName, setFilterName] = React.useState<string>("");
  const [filterNameError, setFilterNameError] = React.useState(false);

  const filterPresetsApi = useLoadFilterPresets(props.cacheKey, props.listApi);
  const addFilterPresetApi = useAddFilterPreset(props.cacheKey, props.mutateApi);
  const removeFilterPresetApi = useRemoveFilterPreset(props.cacheKey);

  const onFilterCreated = async () => {
    if (filterName.trim() === "") {
      setFilterNameError(true);
      return;
    }
    if (addFilterPresetApi.isLoading) return;
    addFilterPresetApi.mutate({
      title: filterName,
      filterSet: props.filterSet,
      filter: props.instruction
    });
    setFilterName("");
  };

  const onFilterDeleted = async (preset: server.dto.FilterPreset<any>) => {
    if (removeFilterPresetApi.isLoading) return;
    return removeFilterPresetApi.mutate(preset.filterPresetId);
  };

  const onFilterSelected = (preset: server.dto.FilterPreset<any>) => {
    props.onApplyPreset(preset);
    props.onClose();
  };

  const emptyFilterPreset: server.dto.FilterPreset<any> = {
    filterPresetId: null,
    filterName: RESX.SelectionManager.NoFiltersApplied,
    filter: null
  };

  const filters = filterPresetsApi.data ? [emptyFilterPreset, ...filterPresetsApi.data.data] : [emptyFilterPreset];

  return <>
    <div className="popover-title blue-bg white-text padding-large">
      {RESX.SelectionManager.FilterPresets}
    </div>
    <div>

      {filterPresetsApi.isLoading && <Loading />}
      {!filterPresetsApi.isLoading && <>
        <div className="padding" style={{ overflowY: "auto", height: 300 }}>

          {filters.map(preset => <div key={preset.filterPresetId}>
            <div key={preset.filterPresetId} className="userPresetRow s1_filterpreset_item flex align-center gap">
              <div tabIndex={0} className="grow1" onClick={() => { onFilterSelected(preset); }}>
                <div className="s1_presetnamecell">
                  {preset.filterName}
                </div>
              </div>
              {preset.filterPresetId && <PhosphorTrashButton
                layout="in-square"
                onClick={() => onFilterDeleted(preset)} />}
            </div>
          </div>)}

        </div>

        <div className="s1_filterpreset_newbox flex align-center">
          <div className="grow1">
            <InputBase
              focusOnMount
              onEnter={onFilterCreated}
              placeholder={RESX.SelectionManager.AddPreset}
              value={filterName}
              onChange={newValue => {
                setFilterName(newValue);
                setFilterNameError(false);
              }}
              maxLength={serverReference.constants.filterPreset.maxTitleLength}
              error={filterNameError}
            />
          </div>
          <div>
            {!filterPresetsApi.isLoading && <PhosphorFloppyButton
              layout="in-square"
              onClick={onFilterCreated}
            />}
            {filterPresetsApi.isLoading && <Loading />}
          </div>
        </div>
      </>}

    </div>
  </>;
};

export default FilterPresetContent;